import styled from 'styled-components'
import { zoning_indice_sources } from '@popety-io/types'

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
  padding: ${({ theme }) => theme.spacing.medium};
`

export const ModalContent = styled.div<{ $hasPDF?: boolean }>`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  max-width: ${({ $hasPDF }) => ($hasPDF ? '1400px' : '600px')};
  width: 100%;
  max-height: 85vh;
  display: ${({ $hasPDF }) => ($hasPDF ? 'grid' : 'block')};
  grid-template-columns: ${({ $hasPDF }) => ($hasPDF ? '40% 60%' : 'none')};
  grid-template-rows: ${({ $hasPDF }) => ($hasPDF ? 'auto 1fr' : 'none')};
  overflow-x: hidden;

  ${({ theme }) => theme.breakpoints?.tablet} {
    max-width: 95vw;
    grid-template-columns: ${({ $hasPDF }) => ($hasPDF ? '1fr' : 'none')};
    grid-template-rows: ${({ $hasPDF }) =>
      $hasPDF ? 'auto auto 1fr' : 'none'};
  }
`

export const ModalHeader = styled.div<{ $hasPDF?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.large};
  grid-column: ${({ $hasPDF }) => ($hasPDF ? '1 / -1' : 'auto')};
  border-bottom: ${({ $hasPDF, theme }) =>
    $hasPDF ? `1px solid ${theme.colors.border.default}` : 'none'};
`

export const DetailsPanel = styled.div<{ $hasPDF?: boolean }>`
  padding: ${({ theme }) => theme.spacing.large};
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.background.secondary}10;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: ${({ theme }) => theme.borderRadius.small};

    &:hover {
      background: ${({ theme }) => theme.colors.text.tertiary};
    }
  }
`

export const PDFViewerPanel = styled.div`
  height: 100%;
  min-height: 500px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-left: 1px solid ${({ theme }) => theme.colors.border.default};
  overflow: hidden;

  ${({ theme }) => theme.breakpoints?.tablet} {
    border-left: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border.default};
  }
`

export const ModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`

export const CloseButton = styled.button`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing.xsmall};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

export const ModalBody = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.6;
`

export const ValueDisplay = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const ValueLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const ValueText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const SourceBadge = styled.div<{ $source: zoning_indice_sources }>`
  display: inline-block;
  padding: ${({ theme }) => `${theme.spacing.xsmall} ${theme.spacing.small}`};
  background: ${({ theme, $source }) =>
    $source === 'estimate'
      ? theme.colors.warningLight
      : $source === 'manual'
        ? theme.colors.successLight
        : theme.colors.primaryLight};
  color: ${({ theme, $source }) =>
    $source === 'estimate'
      ? theme.colors.warning
      : $source === 'manual'
        ? theme.colors.success
        : theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin-top: ${({ theme }) => theme.spacing.small};
`

export const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const SectionTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.small} 0;
`

export const ReferenceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

export const ReferenceItem = styled.li`
  padding: ${({ theme }) => `${theme.spacing.xsmall} 0`};
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.body};

  strong {
    min-width: 120px;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  }

  span {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

export const DocumentLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  transition: color ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primaryHover};
    text-decoration: underline;
  }
`

export const WarningBox = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.medium};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.warningLight};
  border-left: 4px solid ${({ theme }) => theme.colors.warning};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: 1.6;
`

export const AdditionalInfo = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.6;
  font-style: italic;
`
